import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";

import { UserAnalyticsService } from "@analytics/index";

import TabGroup from "@components/Tabs/TabGroup";

import { CompanyType } from "@interfaces/company.type";

import theme from "@theme/index";

import { tableStyles } from "../Table/tableStyles";
import TabPanel from "../Tabs/TabPanel";

import AiInsights from "./AiInsights";
import CompanyDetailsDrawerHeader from "./CompanyDetailsDrawerHeader";
import CompanyDetailsDrawerStyles from "./CompanyDetailsDrawerStyles";
import FundingEventsTable from "./FundingEventsTable";
import Overview from "./Overview";
import PatentTable from "./PatentTable";
import Sources from "./Sources";

export interface ITab {
  disabled?: boolean;
  label: string;
  new?: boolean;
  tooltip?: string;
}

const handleTabs = (company: CompanyType, isSmartSearch: boolean) => {
  const baseTabs = [
    { condition: true, tab: { label: "Overview" } },
    { condition: company.hasSources, tab: { label: "Data Sources" } },
    { condition: company.fundingEvents && company.fundingEvents.length > 0, tab: { label: "Funding" } },
    { condition: company.patentSimilarityScore, tab: { label: "✨ AI Insights" } },
    { condition: true, tab: { label: "News" } },
  ];

  const patentTab = { condition: company.hasPatents, tab: { label: "Patents" } };

  if (isSmartSearch) {
    return [patentTab, ...baseTabs].filter((item) => item.condition).map((item) => item.tab);
  } else {
    // Insert the patentTab at the third position
    baseTabs.splice(2, 0, patentTab);
    return baseTabs.filter((item) => item.condition).map((item) => item.tab);
  }
};

interface ICompanyDisplayProperties {
  close: () => void;
  company: CompanyType;
  getInsight?: (companyId: CompanyType["id"]) => void;
  isInsightLoading?: boolean;
  isSmartSearch?: boolean;
}

export default function CompanyDisplay({
  close,
  company,
  getInsight,
  isInsightLoading = false,
  isSmartSearch = false,
}: ICompanyDisplayProperties) {
  const headerReference = useRef<HTMLDivElement>(null);
  const tabsReference = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState(0);

  const location = useLocation();

  const [tabIndex, setTabIndex] = useState(0);

  const tabs: ITab[] = useMemo(() => handleTabs(company, isSmartSearch), [company, location.pathname]);

  const getInsightIfNeeded = useCallback(() => {
    if (!!company.id && !company.insight && getInsight) {
      getInsight(company.id);
    }
  }, [company.id, company.insight, getInsight]);

  /**
   * Handles the tab switch event.
   * It tracks the event, sets the new tab index and fetches insights if needed.
   * @param index - The index of the tab to switch to.
   */
  const handleTabSwitch = useCallback(
    (index: number) => {
      const currentTab = tabs[index];
      if (!currentTab) return;
      UserAnalyticsService.trackCompanyProfileTab(currentTab.label, index);
      if (currentTab.label !== "News") {
        setTabIndex(index);
      }

      if (currentTab.label.includes("AI Insights")) {
        getInsightIfNeeded();
      }

      if (currentTab.label === "News") {
        const searchUrl = `https://www.google.com/search?q="${company.name}"+(collaboration+OR+partnership+OR+grant+OR+patent+OR+licensing+OR+license)&tbm=nws`;
        window.open(searchUrl, "_blank", "noopener,noreferrer");
      }
    },
    [tabs, setTabIndex, company.name, getInsightIfNeeded],
  );

  useEffect(() => {
    const calculateMaxHeight = () => {
      const headerHeight = headerReference.current ? headerReference.current.offsetHeight : 0;
      const tabsHeight = tabsReference.current ? tabsReference.current.offsetHeight : 0;
      const availableHeight = window.innerHeight - headerHeight - tabsHeight - 64;
      setMaxHeight(availableHeight);
    };

    calculateMaxHeight();
    window.addEventListener("resize", calculateMaxHeight);
    return () => window.removeEventListener("resize", calculateMaxHeight);
  }, []);

  return (
    <Box sx={CompanyDetailsDrawerStyles.container}>
      <div ref={headerReference} style={{ width: "100%" }}>
        <CompanyDetailsDrawerHeader close={close} company={company} />
      </div>
      <Grid
        item
        sx={{
          overflow: "hidden",
          p: 3,
          width: "100%",
        }}
      >
        <div ref={tabsReference}>
          <TabGroup index={tabIndex} setIndex={handleTabSwitch} tabs={tabs} />
        </div>
        <Box
          sx={{
            ...tableStyles.root,
            border: `1px solid ${theme.palette.grey[300]}`,
            borderRadius: 1,
            height: "calc(100vh - 168px)",
            maxHeight,
            overflow: "hidden",
          }}
        >
          {/* Overview Tab ====================================================================== */}
          <TabPanel currentIndex={tabIndex} index={tabs.findIndex((tab) => tab.label === "Overview")}>
            <Overview company={company} />
          </TabPanel>
          {/* Patents Tab ====================================================================== */}
          {company.hasPatents && (
            <TabPanel currentIndex={tabIndex} index={tabs.findIndex((tab) => tab.label === "Patents")}>
              <PatentTable companyId={company.id} />
            </TabPanel>
          )}
          {/* Sources Tab ====================================================================== */}
          <TabPanel currentIndex={tabIndex} index={tabs.findIndex((tab) => tab.label === "Data Sources")}>
            <Sources id={company.id} />
          </TabPanel>
          {/* Funding Tab ====================================================================== */}
          <TabPanel currentIndex={tabIndex} index={tabs.findIndex((tab) => tab.label === "Funding")}>
            <FundingEventsTable fundingEvents={company.fundingEvents} />
          </TabPanel>
          {/* AI Insights Tab ====================================================================== */}
          {company.patentSimilarityScore && (
            <TabPanel currentIndex={tabIndex} index={tabs.findIndex((tab) => tab.label === "✨ AI Insights")}>
              <AiInsights company={company} getInsight={getInsight} isInsightLoading={isInsightLoading} />
            </TabPanel>
          )}
          {/* News Tab ====================================================================== */}
          {/* <TabPanel currentIndex={tabIndex} index={tabs.findIndex((tab) => tab.label === "News")}>
            {company.name && <CompanyNews companyId={company.id} companyName={company.name} />}
          </TabPanel> */}
        </Box>
      </Grid>
    </Box>
  );
}
